import { defineMigrations } from "@supersig/store";
import { T } from "@supersig/validate";
import { propertyTypeMap } from "../../primitives";
import { createResourceId, createTextResourceVariantId } from "../records";
/** @public */ export const textResourceProps = {
    // The actual text we're going to replace
    // The baseString is the value inside here
    text: propertyTypeMap["property-text"],
    // The reference to the localization
    keyId: T.integer,
    // What is the id of the provider in the Superwall
    // database
    localizationProviderId: T.integer,
    // Status (only really relevant for variants)
    status: T.literalEnum("UNVERIFIED", "NORMAL", "REVIEWED"),
    // The filename of the key, again to
    // help with the UI
    files: T.arrayOf(T.object({
        // This is the id of the file for which
        // this key is associated with in the provider
        fileId: T.integer,
        // The keyId is the actual primary key
        // but this is helpful for the ui
        keyName: T.string,
        // The path to the file
        filePath: T.string,
        // Whether or not this is the default filename
        // indicating we should hide the filename
        isDefaultFilePath: T.boolean
    })),
    // Whether or not this has been deleted
    isDeleted: T.optional(T.boolean)
};
export const getLocalizationKey = (resource)=>{
    return resource.props.files[0]?.keyName || resource.props.keyId.toString();
};
// id, organizationId, config
// title ->
// id, (provider_type: ('superwall' | 'localize' | 'crowdin), provider_id: )
// provider: superwall:project_id, lokalize:project_id,
// crowdin:project_id, crowdin:project_id:[organization]
// superwall:project_id
// v1;{key};lokalise;{projectIdentifier}
// v1;hello;lokalise;abc-def
// v1;{key}:lokalize1234
// Open in paywall w/o lokalize1234
// v1:{key}:
export const isTextResource = (resource)=>{
    if (!resource) return false;
    if (resource.typeName !== "resource") return false;
    return resource.type === "text";
};
export const isTextResourceId = (id)=>{
    if (!id) return false;
    return id.startsWith("resource:text:");
};
export const isTextResourceVariant = (resource)=>{
    if (!resource) return false;
    if (resource.typeName !== "resource_variant") return false;
    return resource.type === "text";
};
export const isTextResourceVariantId = (id)=>{
    if (!id) return false;
    return id.startsWith("resource_variant:text:");
};
export const textResourceNameFromProps = (props)=>{
    const { keyId, localizationProviderId } = props;
    return `v1;${localizationProviderId};${keyId}`;
};
export const parseTextResourceName = (name)=>{
    const parts = name.split(";");
    if (parts[0] !== "v1") return undefined;
    const [_, _localizationProviderId, ..._keyId] = parts;
    const keyId = parseInt(_keyId.join(";"));
    const localizationProviderId = parseInt(_localizationProviderId);
    if (isNaN(keyId) || isNaN(localizationProviderId)) return undefined;
    return {
        keyId,
        localizationProviderId
    };
};
export const createTextResourceId = (props, { organizationId, scope })=>{
    const type = "text";
    const name = textResourceNameFromProps(props);
    return createResourceId({
        type,
        name,
        organizationId,
        scope
    });
};
export const createTextResource = (props, { description, organizationId, scope, version = 0 })=>{
    const type = "text";
    const name = textResourceNameFromProps(props);
    return {
        id: createResourceId({
            type,
            name,
            organizationId,
            scope
        }),
        typeName: "resource",
        type,
        props: props,
        scope,
        version,
        description,
        organizationId,
        name,
        meta: {}
    };
};
export const createTextResourceVariant = (props, { organizationId, scope, variant, version, variantVersion })=>{
    const type = "text";
    const name = textResourceNameFromProps(props);
    const obj = {
        id: createTextResourceVariantId({
            type,
            name,
            organizationId,
            scope,
            variant
        }),
        typeName: "resource_variant",
        type,
        props: props,
        scope,
        version,
        variant,
        variantVersion,
        organizationId,
        name,
        meta: {}
    };
    return obj;
};
export const castTextResourceVariant = (record)=>{
    return {
        toTextResourceVariantStored () {
            return {
                ...record,
                id: record.id.replace("resource_variant:", "resource_variant_stored:"),
                typeName: "resource_variant_stored"
            };
        }
    };
};
/** @internal */ export const textResourceMigrations = defineMigrations({});
export const SWTextResourceInfo = {
    migrations: textResourceMigrations,
    props: textResourceProps,
    meta: {}
};
